import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BookmarkRecord } from 'fl-bookmarks';
import { ResourceVm } from 'qp-ui-interface';
import { CARD_ASPECT_RATIO } from '../../../utils/cards/card.constants';
import {
  FocusedCardData,
  ScrollMovement,
} from '../interfaces/cards.interfaces';
import { CardsService } from './cards.service';

@Component({
  selector: 'app-cards',
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss'],
})
export class CardsComponent implements OnInit, OnDestroy {
  @Input() aspectRatio: string = '2x3';
  @Input() carouselWidth: number = 0;
  @Input() noOfCardsShown: number = 7;
  @Input() carouselGap: number = 20;
  @Input() resource: ResourceVm = null;
  @Input() isSeries: boolean = false;
  @Input() showPlayIcon: boolean = false;
  @Input() isHalfVisible?: boolean = false;
  @Input() isTitleVisible: boolean;
  @Input() isContinueWatching: boolean = false;
  @Input() isDetailPageCards: boolean = false;
  @Input() index: number = null;
  @Input() bookmark: BookmarkRecord | null = null;
  @Input() lang = 'en';
  @Input() nowPlayingId: string = '';
  @Input() isPlayerPage: boolean = false;
  @Input() verticalIndex: number = 0;
  @Input() isCollection: boolean = false;
  @Input() isEpisodeList: boolean = false;
  @Input() scrollMovement: ScrollMovement = { right: '' };

  @Output() onClickViewAll: EventEmitter<void> = new EventEmitter<void>();
  @Output() focusCard: EventEmitter<FocusedCardData> =
    new EventEmitter<FocusedCardData>();
  @Output() focusOut: EventEmitter<void> = new EventEmitter<void>();

  isViewAll: boolean = false;
  isProgress: boolean = false;
  progressBarWidth: number;
  sectionWidth: string;
  cardWidth: string;
  cardHeight: string;
  canUpdateCurrentResource: boolean = false;
  imageSrc: string = '';
  imageLoaded = false;

  constructor(
    private readonly cardsService: CardsService,
    private readonly route: ActivatedRoute
  ) {
    this.canUpdateCurrentResource = Boolean(
      this.route.snapshot.data.canUpdataCurrentResource
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['carouselWidth']) {
      this.calculateWidth();
    }
    this.setProgress();
  }

  ngOnInit(): void {
    this.calculateWidth();

    this.imageSrc =
      this.resource?.image || '/assets/icons/placeholder_no_image.svg';
    this.setProgress();
    if (this.resource.type === 'view-all') {
      this.isViewAll = true;
    }
    if (this.nowPlayingId === this.resource.id) {
      this.showPlayIcon = false;
    }
  }

  private setProgress() {
    if (this.isContinueWatching) {
      this.isProgress = true;
      this.progressBarWidth = this.resource.offset;
      this.isTitleVisible = true;
    } else if (this.bookmark && this.bookmark.episodeId === this.resource.id) {
      this.isProgress = true;
      this.progressBarWidth = this.bookmark.offset;
    }
  }

  onImageError(event: Event): void {
    const fallback = '/assets/icons/placeholder_no_image.svg'; // or any SVG path you prefer
    const img = event.target as HTMLImageElement;

    if (img && img.src !== fallback) {
      img.src = fallback;
    }
  }

  onImageLoad() {
    this.imageLoaded = true;
  }

  getClassNames(): string {
    return (
      CARD_ASPECT_RATIO.find((card) =>
        this.aspectRatio.includes(card.aspectRatio)
      )?.className || ''
    );
  }

  calculateWidth() {
    const { sectionWidth, cardWidth } = this.cardsService.calculateCardWidth(
      this.carouselWidth,
      this.carouselGap,
      this.noOfCardsShown,
      0.312,
      0.729, // Border thickness 6px if you adjust in css you need change it here as well
      this.isHalfVisible
    );
    this.sectionWidth = `${sectionWidth}px`;
    this.cardWidth = `${cardWidth}px`;
    this.cardHeight = `${this.cardsService.calculateCardHeight(
      cardWidth,
      this.aspectRatio
    )}px`;
  }

  onFocus(resource: ResourceVm) {
    const data: FocusedCardData = {};
    if (
      this.canUpdateCurrentResource &&
      !this.isViewAll &&
      !this.isDetailPageCards
    ) {
      data.resource = resource;
    }
    if (!this.index && !this.isDetailPageCards && !this.isPlayerPage) {
      data.index = this.index;
      data.verticalIndex = this.verticalIndex;
    }
    this.focusCard.emit(data);
  }

  get seasonNumber() {
    return `S${this.resource.seasonNumber} E${this.resource.episodeNumber}`;
  }

  get runningTime() {
    return `${this.resource.runningTimeinMins} Min`;
  }

  get releaseDate() {
    const date = new Date(this.resource.catalogAvailabilityStartDate);
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  ngOnDestroy(): void {
    this.forceClean();
  }

  forceClean(): void {
    this.aspectRatio = null;
    this.carouselWidth = null;
    this.noOfCardsShown = null;
    this.carouselGap = null;
    this.resource = null;
    this.isSeries = null;
    this.showPlayIcon = null;
    this.isHalfVisible = null;
    this.isTitleVisible = null;
    this.isContinueWatching = null;
    this.isDetailPageCards = null;
    this.index = null;
    this.bookmark = null;
    this.lang = null;
    this.nowPlayingId = null;
    this.isPlayerPage = null;
    this.verticalIndex = null;
    this.isCollection = null;
    this.isEpisodeList = null;
    this.onClickViewAll.complete();
    this.focusCard.complete();
    this.focusOut.complete();
    this.scrollMovement = null;
  }
}
